import Vue from "vue";
import Router from "vue-router";
import {adminRoutes} from "@/routes/admin.router";
import {authRouter} from "@/routes/auth.router";
import {agentRouter} from "@/routes/agent.router";
import {qualityRouter} from "@/routes/quality.router";
import {salesManagerRouter} from "@/routes/salesManager.router";
import {fundingManagerRouter} from "@/routes/fundingManager.router";
import {commonRouter} from "@/routes/common.router";
import {accountingRouter} from "@/routes/accounting.router";
import {customerServiceSupervisorRoutes} from "@/routes/customerServiceSupervisor.router";
import {store} from "@/store/index";
import {
  refreshPendingQuestionnaire,
  shouldBlockFundingManagerRoute,
} from "@/utils/fundingQuestionnaireGuard";

Vue.use(Router);

const router = new Router({
  mode: "history",
  routes: [
      ...authRouter,
      ...adminRoutes,
      ...agentRouter,
      ...salesManagerRouter,
      ...fundingManagerRouter,
      ...qualityRouter,
      ...accountingRouter,
      ...customerServiceSupervisorRoutes,
      ...commonRouter,
  ],
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

// Helper function to get cookie value
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// Helper function to check authentication
function isAuthenticated() {
  return !!getCookie('wsataJwt');
}

// Helper function to get role-based redirect path
function getRoleBasedRedirect(userRole) {
  // Customer Service users are stored as role '0' with a separate flag.
  if (userRole === '0' && localStorage.getItem('is_customer_service') === '1') {
    return '/customer-service/requests/all';
  }

  const roleRedirects = {
    '7': '/admin',
    '16': '/customer-service-supervisor',
    '1': '/salesManager', 
    '2': '/funding-manager',
    '5': '/quality/requests/all'
  };

  if (userRole == '8') {
    const accountType = localStorage.getItem('account_type');
    const accountTypeRedirects = {
      '0': '/accounting/tsaheelreports',
      '1': '/accounting/wasatareports', 
    };
    
    return accountTypeRedirects[accountType] || '/accounting/wasatareports';
  }

  return roleRedirects[userRole] || null;
}

router.beforeEach(async (to, from, next) => {
  try {
    const isAuth = isAuthenticated();
    
    if (!isAuth && to.path !== '/login') {
      next('/login');
      return;
    }
    
    if (isAuth && to.path === '/login') {
      next('/');
      return;
    }

    if (to.name === 'all') {
      const userRole = localStorage.getItem('role');
      const redirectPath = getRoleBasedRedirect(userRole);
      
      if (redirectPath) {
        next(redirectPath);
        return;
      }
    }

    if (isAuth && localStorage.getItem('role') === '2') {
      const shouldCheckQuestionnaire =
        store.state.fundingManagerBlockedByQuestionnaire ||
        to.path.startsWith('/funding') ||
        to.path.startsWith('/fundingManager');

      if (shouldCheckQuestionnaire) {
        await refreshPendingQuestionnaire(store.state.fundingManagerBlockedByQuestionnaire);
      }

      if (shouldBlockFundingManagerRoute(to.name)) {
        store.state.showFundingQuestionnaireRequiredAlert = true;
        next({
          name: 'FundingQuestionAndAnswer',
          query: { required: '1' },
        });
        return;
      }
    }

    next();
  } catch (error) {
    console.error('Router beforeEach error:', error);
    next();
  }
});

router.beforeResolve((to, from, next) => {
  Vue.nextTick(() => {
    document.title = to.meta.page_name || "شركة الوساطة العقارية";
  });

  if (from.name && to.name !== from.name) {
    import('@/store').then(({ store }) => {
      store.dispatch('preloader/showFullPageLoading', {
        loadingText: `جاري تحميل ${to.meta.page_name || to.name}...`,
        subText: 'يرجى الانتظار',
        spinnerType: 'wave',
        autoHide: true,
        autoHideDelay: 1500
      });
    });
  }

  next();
});

router.onError((error) => {
  console.error('Router navigation error:', error);
});

router.afterEach(() => {
  import('@/store').then(({ store }) => {
    setTimeout(() => {
      store.dispatch('preloader/hidePreloader');
    }, 500);
  });
});

export default router;
